import { defineStore } from 'pinia'
import { Capacitor } from '@capacitor/core'
import client from '@/api/client'
import { storage } from '@/api/preferences'
import { disconnectEcho } from '@/api/echo'
import { identify, reset as resetPostHog } from '@/api/posthog'
import { registerPush, unregisterPush } from '@/composables/usePushNotifications'
import { checkMicPermission } from '@/composables/usePermissions'
import router from '@/router'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: null,
    loading: false,
    currentTeamId: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentTeam: (state) => {
      const teams = state.user?.teams ?? []
      if (!teams.length) return null
      if (state.currentTeamId) {
        const match = teams.find(t => t.id === state.currentTeamId)
        if (match) return match
      }
      return teams[0]
    },
  },

  actions: {
    async hydrate() {
      this.token = await storage.get('pavemi_token')
      const storedTeamId = await storage.get('pavemi_current_team_id')
      this.currentTeamId = storedTeamId ? Number(storedTeamId) : null
      // Restore the last-known user so a cold start opens straight into the
      // app instead of flashing login while /me is in flight. fetchMe()
      // refreshes it afterwards.
      const storedUser = await storage.get('pavemi_user')
      if (storedUser) {
        try {
          this.user = JSON.parse(storedUser)
        } catch {
          await storage.remove('pavemi_user')
        }
      }
    },

    async setActiveTeam(teamId) {
      const id = Number(teamId)
      if (!id) return
      const belongs = (this.user?.teams ?? []).some(t => t.id === id)
      if (!belongs) return
      this.currentTeamId = id
      await storage.set('pavemi_current_team_id', String(id))
    },

    async createWorkspace(name) {
      const { data } = await client.post('/teams', { name })
      // Re-fetch /me so user.teams includes the new team with its pivot.role,
      // then switch into it. setActiveTeam validates membership against the
      // refreshed list.
      await this.fetchMe()
      await this.setActiveTeam(data.data.id)
      return data.data
    },

    async register(payload) {
      this.loading = true
      try {
        const { data } = await client.post('/register', payload, { silent: true })
        await this.setSession(data.user, data.token)

        // On native mobile: check if we should show the permissions onboarding
        // screen. We set a flag so the caller (Register.vue) can redirect there
        // instead of straight to the dashboard.
        if (Capacitor.isNativePlatform()) {
          const mic = await checkMicPermission()
          if (mic === 'prompt') {
            return { ...data, needsOnboarding: true }
          }
        }

        return data
      } finally {
        this.loading = false
      }
    },

    async login(payload) {
      this.loading = true
      try {
        const { data } = await client.post('/login', payload, { silent: true })
        await this.setSession(data.user, data.token)
        return data
      } finally {
        this.loading = false
      }
    },

    async loginWithGoogle(credential) {
      this.loading = true
      try {
        const { data } = await client.post('/auth/google', { credential }, { silent: true })
        await this.setSession(data.user, data.token)
        return data
      } finally {
        this.loading = false
      }
    },

    async loginWithMicrosoft(token) {
      this.loading = true
      try {
        const { data } = await client.post('/auth/microsoft', { token }, { silent: true })
        await this.setSession(data.user, data.token)
        return data
      } finally {
        this.loading = false
      }
    },

    async forgotPassword(email) {
      const { data } = await client.post('/auth/forgot-password', { email }, { silent: true })
      return data
    },

    async resetPassword(payload) {
      const { data } = await client.post('/auth/reset-password', payload, { silent: true })
      return data
    },

    async fetchMe() {
      if (!this.token) return null
      try {
        const { data } = await client.get('/me', { silent: true })
        this.user = data.user
        await storage.set('pavemi_user', JSON.stringify(data.user))
        // Si el currentTeamId persistido ya no pertenece al usuario (lo removieron
        // del team o el team fue borrado), caemos al primer equipo disponible.
        if (this.currentTeamId) {
          const stillBelongs = (data.user?.teams ?? []).some(t => t.id === this.currentTeamId)
          if (!stillBelongs) {
            this.currentTeamId = null
            await storage.remove('pavemi_current_team_id')
          }
        }
        return data.user
      } catch (err) {
        // Solo cerramos sesión si el servidor rechazó el token explícitamente.
        // Un fallo transitorio (offline, timeout, 5xx, o la red aún no lista en
        // un cold start móvil) NO debe destruir el token persistido — si no,
        // reabrir la app sin red desloguea al usuario.
        if (err?.response?.status === 401) {
          await this.clearSession()
        }
        return null
      }
    },

    // Edición del propio perfil (hoy solo el nombre). El backend devuelve el
    // user completo, así que reemplazamos el de la store y su copia persistida
    // para que sidebar, header y avatares reflejen el cambio sin refetch.
    async updateProfile(payload) {
      const { data } = await client.put('/me/profile', payload, { silent: true })
      this.user = data.user
      await storage.set('pavemi_user', JSON.stringify(data.user))
      return data.user
    },

    async logout() {
      try {
        await client.post('/logout', null, { silent: true })
      } catch {
        // ignore
      }
      await this.clearSession()
    },

    async setSession(user, token) {
      this.user = user
      this.token = token
      await storage.set('pavemi_token', token)
      await storage.set('pavemi_user', JSON.stringify(user))
      if (user?.id) identify(user.id, { email: user.email })

      // Register for push notifications on mobile (no-op on web).
      // Fire-and-forget: don't block login flow if push registration fails.
      registerPush((data) => {
        if (data?.task_id) {
          router.push({ path: '/my-tasks', query: { task: data.task_id } })
        }
      }).catch((err) => console.error('Push registration failed', err))
    },

    async clearSession() {
      // Best-effort: deregister push token before clearing local state.
      try {
        const lastPushToken = await storage.get('pavemi_last_push_token')
        if (lastPushToken) {
          await unregisterPush(lastPushToken)
          await storage.remove('pavemi_last_push_token')
        }
      } catch (err) {
        console.error('Failed to unregister push token on logout', err)
      }

      this.user = null
      this.token = null
      this.currentTeamId = null
      await storage.remove('pavemi_token')
      await storage.remove('pavemi_user')
      await storage.remove('pavemi_current_team_id')
      disconnectEcho()
      resetPostHog()
    },
  },
})
